import React, { useEffect } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";

import BaseModal from "../../../components/ui/BaseModal";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseButton from "../../../components/ui/BaseButton";

import { showToast } from "../../../store/toast/toastSlice";
import { createQualification } from "../../../services/api/qualification";

const validationSchema = yup.object({
  name: yup.string().trim().required("Qualification type is required"),
});

export default function AddEditQualificationModal({
  open,
  close,
  refetchQualifications,
}) {
  const dispatch = useDispatch();

  const { mutateAsync: createMutation, isPending } = useMutation({
    mutationFn: createQualification,
  });

  const handleCreate = async () => {
    try {
      await createMutation({ name: formik.values.name.trim() });
      dispatch(
        showToast({ message: "Qualification type created", type: "success" })
      );
      refetchQualifications?.();
      formik.resetForm();
      close(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        })
      );
    }
  };

  const formik = useFormik({
    initialValues: { name: "" },
    validationSchema,
    onSubmit: handleCreate,
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    if (!open) formik.resetForm();
  }, [open]);

  return (
    <form>
      <BaseModal
        className="-top-[200px] overflow-auto"
        open={open}
        close={close}
      >
        <div className="py-7 px-12 xl:w-[1000px] lg:w-[900px] sm:w-[700px]">
          <h6 className="text-dark font-normal border-b pb-1">
            Create New Qualification
          </h6>

          <div className="grid grid-cols-2 gap-x-8 mt-8">
            <div className="col-span-2">
              <p className="font-semibold mb-2">Qualification Type</p>
              <BaseTextField
                id="name"
                name="name"
                placeholder="e.g., Electrical License"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>
          </div>
        </div>

        <div
          className="d-flex content-center bg-white py-6 mt-6"
          style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
        >
          <BaseButton
            className="px-[28px] py-[5px] bg-light text-primary !hover:text-primary"
            variant="outlined"
            color="primary"
            style={{ marginRight: "16px" }}
            onClick={() => close(false)}
          >
            Cancel
          </BaseButton>

          <BaseButton
            loaderSize={24}
            loaderColor="primary"
            className="px-[12px] py-[5px]"
            color="primary"
            onClick={formik.handleSubmit}
            disabled={!formik.values?.name?.trim() || isPending}
          >
            Create
          </BaseButton>
        </div>
      </BaseModal>
    </form>
  );
}
